import { useRef, useState } from 'react';
import { SPORTS } from '../data/sports';

export default function Sports() {
  const [activeId, setActiveId] = useState(SPORTS[0].id);
  const listRef = useRef<HTMLDivElement>(null);
  const active = SPORTS.find((s) => s.id === activeId) ?? SPORTS[0];

  // arrow keys move along the tab list, matching the tablist role
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = SPORTS.findIndex((s) => s.id === activeId);
    const next = SPORTS[(i + (e.key === 'ArrowRight' ? 1 : -1) + SPORTS.length) % SPORTS.length];
    setActiveId(next.id);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`[data-sport="${next.id}"]`)
      ?.focus();
  };

  return (
    <section
      id="sports"
      className="sec"
      style={{ background: 'linear-gradient(180deg, #070809, #0B0E0F 40%, #070809)' }}
    >
      <div className="shell">
        <div style={{ maxWidth: '660px' }}>
          <p className="eyebrow">Any surface, any sport</p>
          <h2 className="h-lg" style={{ marginTop: '18px' }}>
            Built Around Your Sport.
          </h2>
          <p className="lead" style={{ marginTop: '22px' }}>
            Court shapes, slot lengths, formats and pricing rules change by sport. PlaySlot ships
            with all of them, and your team sets the rules for every court in the facility.
          </p>
        </div>

        <div className="sport-layout">
          <div
            className="sport-list"
            role="tablist"
            aria-label="Choose a sport"
            ref={listRef}
            onKeyDown={onKeyDown}
          >
            {SPORTS.map((sport) => (
              <button
                key={sport.id}
                type="button"
                className="sport-btn"
                role="tab"
                data-sport={sport.id}
                aria-selected={sport.id === activeId}
                aria-controls="sport-panel"
                onClick={() => setActiveId(sport.id)}
              >
                <span className="dot" aria-hidden="true" />
                <svg
                  className="glyph"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: sport.glyph }}
                />
                {sport.name}
              </button>
            ))}
          </div>

          <div className="sport-panel" id="sport-panel" role="tabpanel">
            {/* key on the sport id restarts the crossfade whenever the sport changes */}
            <div className="sport-inner" key={active.id}>
              <div>
                <p
                  className="mono swap"
                  style={{ fontSize: '11px', letterSpacing: '.18em', color: 'var(--lime)' }}
                >
                  {active.tag}
                </p>
                <h3
                  className="swap"
                  style={{
                    fontSize: 'clamp(30px, 3.4vw, 44px)',
                    fontWeight: 800,
                    letterSpacing: '-.03em',
                    marginTop: '10px',
                  }}
                >
                  {active.name}
                </h3>
                <p
                  className="swap"
                  style={{
                    fontSize: '15.5px',
                    lineHeight: 1.6,
                    color: 'var(--ink-2)',
                    marginTop: '12px',
                    maxWidth: '420px',
                  }}
                >
                  {active.blurb}
                </p>

                <div className="court" style={{ aspectRatio: active.ratio }}>
                  {active.lines.map((line, i) => (
                    <span key={i} className="ln" style={parseInlineStyle(line)} />
                  ))}
                </div>

                <p
                  className="mono"
                  style={{
                    fontSize: '10px',
                    letterSpacing: '.12em',
                    color: 'var(--ink-5)',
                    marginTop: '12px',
                  }}
                >
                  COURT MAP USED ACROSS BOOKING, SCHEDULING AND REPORTING
                </p>
              </div>

              <div className="sport-stats swap">
                {active.stats.map((stat, i) => (
                  <div
                    key={stat.k}
                    className={i === active.stats.length - 1 ? 'sport-stat is-next' : 'sport-stat'}
                  >
                    <div className="k">{stat.k}</div>
                    <div className="v">{stat.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** turns "left:50%;top:0" into a React style object */
function parseInlineStyle(css: string): React.CSSProperties {
  const style: Record<string, string> = {};
  css.split(';').forEach((decl) => {
    const [prop, ...rest] = decl.split(':');
    if (!prop || !rest.length) return;
    const key = prop.trim().replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    style[key] = rest.join(':').trim();
  });
  return style as React.CSSProperties;
}
